// Coverage-aware order card: the per-row `coverage` object and the card's
// `coverage_summary` the backend stamps (coverage_card.service), and the
// pure helpers that turn them into a badge, a cost label and a total.
// Advisory only — nothing here changes what gets placed.

export type CoverageState = "covered" | "prior_auth" | "not_covered" | "out_of_network" | "unknown" | "checking";

export interface RowCoverage {
  state: CoverageState;
  cpt?: string;
  category?: string;
  // What the practice bills (fee schedule charge × typical units), what the
  // patient's share is figured on, and the split for this plan.
  billed?: number;
  allowed?: number;
  plan_pays?: number;
  patient_pays?: number;
  cost_basis?: "copay" | "deductible" | "coinsurance" | "not_covered" | "formulary_tier";
  cost_detail?: string;
  // Where the dollar figure came from: the practice's fee schedule (its
  // allowed amount, or the charge alone). Nothing else prices a row.
  rate_source?: "fee_schedule_allowed" | "fee_schedule_charge";
  schedule_item?: string;
  unit?: string;
  reason?: string;
  auth?: {
    policy_ref?: string;
    criteria_met?: string[];
    criteria_missing?: string[];
    packet_ready?: boolean;
    // "payer" = the 271 said so; "practice_rule" = a seeded rule, unconfirmed.
    source?: "payer" | "practice_rule";
    note?: string | null;
  };
  abn_eligible?: boolean;
  covering_dx?: { code: string; label: string; in_note: boolean };
  sources?: string[];
  checked_at?: string;
  confidence?: "high" | "medium" | "low";
}

export interface CoveragePolicy {
  payer_name: string;
  tier: string;
  status: string;
  relationship?: string;
  end_date?: string | null;
  member_id_last4?: string | null;
}

export interface CoverageFeeSchedule {
  name: string;
  description?: string;
  truncated?: boolean;
  rates?: number;
}

export interface CoverageSummary {
  state: "verified" | "unverified" | "inactive" | "self_pay" | "no_insurance";
  fee_schedule?: CoverageFeeSchedule | null;
  payer_name?: string;
  plan_family?: string;
  plan_type?: string | null;
  plan_status?: string | null;
  plan_end?: string | null;
  office_copay?: number | null;
  ehr_eligibility?: string | null;
  ehr_status?: string | null;
  secondary?: string[];
  relationship?: string;
  policies?: CoveragePolicy[];
  deductible?: { total: number; met: number } | null;
  oop?: { total: number; met: number } | null;
  source?: string | null;
  verified_at?: string | null;
  fallback_reason?: string | null;
  // Why the plan could not be verified (payer unmapped, no key, no answer).
  unverified_reason?: string | null;
  requester?: string | null;
  payer_id?: string | null;
  referral_required?: boolean;
  reason?: string;
  checked_at?: string;
}

export const COVERAGE_BADGES: Record<CoverageState, { label: string; color: string; bg: string; border: string }> = {
  covered: { label: "Covered", color: "#166534", bg: "#DCFCE7", border: "#BBF7D0" },
  prior_auth: { label: "Prior auth", color: "#92400E", bg: "#FEF3C7", border: "#FDE68A" },
  not_covered: { label: "Not covered", color: "#991B1B", bg: "#FEE2E2", border: "#FECACA" },
  out_of_network: { label: "Out of network", color: "#991B1B", bg: "#FEE2E2", border: "#FECACA" },
  unknown: { label: "Unknown", color: "#4B5563", bg: "#F3F4F6", border: "#E5E7EB" },
  checking: { label: "Checking…", color: "#4B5563", bg: "#F3F4F6", border: "#E5E7EB" },
};

const money = (v: number): string => {
  const rounded = Math.round(v);
  return `$${rounded.toLocaleString("en-US")}`;
};

// "$35" (a copay, or a figure from the fee schedule's allowed amount:
// exact), "~$44" (figured from the charge alone or a Medicare estimate), or
// "" when there is no estimate.
export const costLabel = (c: RowCoverage | undefined): string => {
  if (!c || c.patient_pays == null) return "";
  if (c.cost_basis === "copay" || c.confidence === "high") return money(c.patient_pays);
  return `~${money(c.patient_pays)}`;
};

// Sum over the checked rows that carry a figure; null when none does.
// `exact` only when every counted row is.
export const selectedCostTotal = (
  rows: ReadonlyArray<{ index: number; coverage?: RowCoverage | null }>,
  selected: ReadonlySet<number> | null | undefined,
): { billed: number; patient_pays: number; exact: boolean } | null => {
  let billed = 0;
  let patient = 0;
  let seen = false;
  let exact = true;
  for (const r of rows) {
    const c = r.coverage;
    if ((selected && !selected.has(r.index)) || !c || c.patient_pays == null) continue;
    patient += c.patient_pays;
    billed += c.billed ?? 0;
    seen = true;
    if (!(c.cost_basis === "copay" || c.confidence === "high")) exact = false;
  }
  return seen ? { billed, patient_pays: patient, exact } : null;
};

export const totalLabel = (t: { patient_pays: number; exact?: boolean } | null): string => {
  if (!t) return "";
  return t.exact ? money(t.patient_pays) : `~${money(t.patient_pays)}`;
};

const RELATIONSHIP_WORD: Record<string, string> = {
  spouse: "spouse",
  child: "child",
  parent: "parent",
  partner: "partner",
  other: "dependent",
  injured: "injured party",
};

// The one-line header strip: payer, plan, deductible progress. Empty parts
// are dropped so a thin summary still reads cleanly.
export const summaryParts = (s: CoverageSummary | null | undefined): string[] => {
  if (!s) return [];
  if (s.state === "self_pay") return ["Self-pay patient", "no coverage lookup"];
  if (s.state === "no_insurance") return ["No insurance on file"];
  const parts: string[] = [];
  const payer = [s.payer_name, s.plan_type].filter(Boolean).join(" · ");
  if (payer) parts.push(payer);
  if (s.state === "inactive") {
    parts.push(
      s.ehr_status === "terminated" || s.ehr_status === "expired"
        ? `${s.ehr_status} in the EHR`
        : s.plan_status === "inactive"
          ? "payer reports inactive"
          : "marked inactive in the EHR",
    );
  }
  if (s.relationship && s.relationship !== "self") {
    parts.push(`covered as ${RELATIONSHIP_WORD[s.relationship] ?? "dependent"} of subscriber`);
  }
  if (s.referral_required) parts.push("PCP referral required");
  if (s.deductible && s.deductible.total > 0) {
    parts.push(`deductible ${money(s.deductible.met)} of ${money(s.deductible.total)} met`);
  }
  if (s.oop && s.oop.total > 0) parts.push(`out-of-pocket ${money(s.oop.met)} of ${money(s.oop.total)}`);
  if (s.office_copay != null && s.office_copay > 0) parts.push(`office copay ${money(s.office_copay)}`);
  if (s.secondary && s.secondary.length) parts.push(`secondary: ${s.secondary.join(", ")}`);
  return parts;
};

const whenLabel = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? `today ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// Where the figures came from, for the small trailing caption.
export const summaryCaption = (s: CoverageSummary | null | undefined): string => {
  if (!s || s.state === "self_pay" || s.state === "no_insurance") return "";
  const bits: string[] = [];
  if (s.ehr_eligibility) bits.push(`EHR eligibility: ${s.ehr_eligibility.toLowerCase()}`);
  const when = whenLabel(s.verified_at);
  switch (s.source) {
    case "stedi_271":
      bits.push(when ? `verified with payer ${when}` : "verified with payer");
      break;
    case "stedi_271_cached":
      bits.push(when ? `payer verified ${when}` : "payer verified (cached)");
      break;
    case "stedi_271_stale":
      bits.push(when ? `last payer answer ${when} · live check failed` : "last payer answer · live check failed");
      break;
    default:
      break;
  }
  if (s.requester === "default") bits.push("sent under the default NPI");
  if (s.fee_schedule?.name) {
    bits.push(`fees from ${s.fee_schedule.name}${s.fee_schedule.truncated ? " (partial pull)" : ""}`);
  } else {
    bits.push("no fee schedule loaded");
  }
  bits.push("estimates only");
  return bits.join(" · ");
};
